import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import type {
  AssetEquityDetailsModel,
  AssetModel,
  AssetPriceModel,
  AssetProviderExtensionModel,
  AssetTaxonomyModel,
} from "./db/models";
import {
  normalizeAssetDomainAssetIds,
  toAssetDomainCollectionId,
  toAssetProviderExtensionId,
} from "./db/models";

export type AssetOverlaysBatchOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["getAssetOverlaysBatch"]["query"]>
>;
export type AssetDetailsOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["getAssetDetails"]["query"]>
>;
export type AssetBasicMarketDataBatchOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["assets"]["getBasicMarketDataForAssets"]["query"]
  >
>;
export type AssetTokenMetadataBatchOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["getTokenMetadataBatch"]["query"]>
>;
export type BrowseStocksOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["browseStocks"]["query"]>
>;
export type BrowseStocksForSelectionOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["assets"]["browseStocksForSelection"]["query"]
  >
>;
export type StocksPageOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["getStocksPage"]["query"]>
>;

type AssetOverlaysBatchItem = AssetOverlaysBatchOutput["items"][number];
export type BrowseAssetItem =
  | BrowseStocksOutput["items"][number]
  | BrowseStocksForSelectionOutput["items"][number];
export type StocksPageRow = StocksPageOutput["rows"][number];

type AssetOverlayItem = {
  assetId: string;
  equityDetails: AssetOverlaysBatchItem["equityDetails"];
  metadata: AssetOverlaysBatchItem["metadata"];
  providerExtensions: AssetOverlaysBatchItem["providerExtensions"];
  taxonomy: AssetOverlaysBatchItem["taxonomy"];
};

export interface NormalizedAssetDomainGraph {
  asset: AssetModel[];
  assetEquityDetails: AssetEquityDetailsModel[];
  assetPrice: AssetPriceModel[];
  assetProviderExtensions: AssetProviderExtensionModel[];
  assetTaxonomy: AssetTaxonomyModel[];
}

export function normalizeAssetOverlayBatchResponse(input: {
  response: AssetOverlaysBatchOutput;
  updatedAt: number;
}): NormalizedAssetDomainGraph {
  return normalizeAssetOverlayItems({
    items: input.response.items,
    updatedAt: input.updatedAt,
  });
}

export function normalizeAssetDetailsResponse(input: {
  assetId: string;
  response: AssetDetailsOutput;
  updatedAt: number;
}): NormalizedAssetDomainGraph {
  const asset = toAssetModelFromAssetDetails(input);
  const assetPrice = toAssetPriceModelFromAssetDetails(input);

  const overlays = normalizeAssetOverlayItems({
    items: [
      {
        assetId: input.assetId,
        equityDetails: input.response.equityDetails,
        metadata: input.response.metadata,
        providerExtensions: {
          ondo:
            input.response.marketData.ondo ??
            input.response.metadata?.ondo ??
            null,
        },
        taxonomy: input.response.taxonomy,
      },
    ],
    updatedAt: input.updatedAt,
  });

  return {
    ...overlays,
    asset: asset ? [asset] : overlays.asset,
    assetPrice: assetPrice ? [assetPrice] : [],
  };
}

export function normalizeAssetTokenMetadataBatchResponse(input: {
  response: AssetTokenMetadataBatchOutput;
  updatedAt: number;
}): AssetModel[] {
  const assetModels: AssetModel[] = [];

  for (const [responseAssetId, metadata] of Object.entries(
    input.response.tokens,
  )) {
    const assetId = normalizeResponseAssetId(responseAssetId);
    if (!assetId) {
      continue;
    }

    const parsedAssetId = parseAssetId(assetId);
    if (
      !parsedAssetId ||
      !metadata ||
      metadata.name.trim().length === 0 ||
      metadata.symbol.trim().length === 0
    ) {
      continue;
    }

    assetModels.push({
      address: parsedAssetId.address,
      assetId,
      chainId: parsedAssetId.chainId,
      decimals: metadata.decimals,
      id: toAssetDomainCollectionId(assetId),
      imageUrl: metadata.iconUrl ?? null,
      name: metadata.name,
      symbol: metadata.symbol,
      updatedAt: input.updatedAt,
    });
  }

  return assetModels;
}

export function normalizeAssetBasicMarketDataBatchResponse(input: {
  response: AssetBasicMarketDataBatchOutput;
  updatedAt: number;
}): AssetPriceModel[] {
  const assetPrices: AssetPriceModel[] = [];

  for (const [responseAssetId, marketData] of Object.entries(
    input.response.assetsWithBasicMarketData,
  )) {
    const assetId = normalizeResponseAssetId(
      marketData?.assetId ?? responseAssetId,
    );
    if (!assetId || !marketData) {
      continue;
    }

    assetPrices.push(
      toAssetPriceModel({
        assetId,
        buyCount1: marketData.buyCount1,
        change1: marketData.change1,
        change24: marketData.change24,
        circulatingMarketCap: marketData.circulatingMarketCap,
        high1: marketData.high1,
        liquidity: marketData.liquidity,
        marketCap: marketData.marketCap,
        priceUsd: marketData.priceUSD,
        quoteToken: marketData.quoteToken ?? null,
        txnCount1: marketData.txnCount1,
        uniqueTransactions1: marketData.uniqueTransactions1,
        updatedAt: input.updatedAt,
        volume1: marketData.volume1,
        volume24: marketData.volume24,
      }),
    );
  }

  return assetPrices;
}

export function normalizeBrowseAssetItems(input: {
  items: readonly BrowseAssetItem[];
  updatedAt: number;
}): NormalizedAssetDomainGraph {
  const asset: AssetModel[] = [];
  const assetPrice: AssetPriceModel[] = [];
  const assetProviderExtensions: AssetProviderExtensionModel[] = [];
  const assetTaxonomy: AssetTaxonomyModel[] = [];

  for (const item of input.items) {
    const assetModel = toAssetModelFromBrowseAssetItem(item, input.updatedAt);
    if (assetModel) {
      asset.push(assetModel);
    }

    const assetPriceModel = toAssetPriceModelFromBrowseAssetItem(
      item,
      input.updatedAt,
    );
    if (assetPriceModel) {
      assetPrice.push(assetPriceModel);
    }

    const taxonomyModel = toBrowseAssetTaxonomyModel(item, input.updatedAt);
    if (taxonomyModel) {
      assetTaxonomy.push(taxonomyModel);
    }

    const providerExtensionModels = toBrowseAssetProviderExtensionModels(
      item,
      input.updatedAt,
    );
    if (providerExtensionModels.length > 0) {
      assetProviderExtensions.push(...providerExtensionModels);
    }
  }

  return {
    asset,
    assetEquityDetails: [],
    assetPrice,
    assetProviderExtensions,
    assetTaxonomy,
  };
}

export function normalizeStocksPageRows(input: {
  rows: readonly StocksPageRow[];
  updatedAt: number;
}): NormalizedAssetDomainGraph {
  const asset: AssetModel[] = [];
  const assetPrice: AssetPriceModel[] = [];
  const assetProviderExtensions: AssetProviderExtensionModel[] = [];

  for (const row of input.rows) {
    const assetModel = toAssetModelFromStocksPageRow(row, input.updatedAt);
    if (assetModel) {
      asset.push(assetModel);
    }

    const assetPriceModel = toAssetPriceModelFromStocksPageRow(
      row,
      input.updatedAt,
    );
    if (assetPriceModel) {
      assetPrice.push(assetPriceModel);
    }

    const providerExtensionModels = toStocksPageProviderExtensionModels(
      row,
      input.updatedAt,
    );
    if (providerExtensionModels.length > 0) {
      assetProviderExtensions.push(...providerExtensionModels);
    }
  }

  return {
    asset,
    assetEquityDetails: [],
    assetPrice,
    assetProviderExtensions,
    assetTaxonomy: [],
  };
}

function normalizeAssetOverlayItems(input: {
  items: readonly AssetOverlayItem[];
  updatedAt: number;
}): NormalizedAssetDomainGraph {
  const asset: AssetModel[] = [];
  const assetEquityDetails: AssetEquityDetailsModel[] = [];
  const assetProviderExtensions: AssetProviderExtensionModel[] = [];
  const assetTaxonomy: AssetTaxonomyModel[] = [];

  for (const item of input.items) {
    const normalizedItem = normalizeAssetOverlayItem(item);
    if (!normalizedItem) {
      continue;
    }

    const assetModel = toAssetModelFromOverlayItem(
      normalizedItem,
      input.updatedAt,
    );
    if (assetModel) {
      asset.push(assetModel);
    }

    const taxonomyModel = toAssetTaxonomyModel(normalizedItem, input.updatedAt);
    if (taxonomyModel) {
      assetTaxonomy.push(taxonomyModel);
    }

    const equityDetailsModel = toAssetEquityDetailsModel(
      normalizedItem,
      input.updatedAt,
    );
    if (equityDetailsModel) {
      assetEquityDetails.push(equityDetailsModel);
    }

    const providerExtensionModels = toAssetProviderExtensionModels(
      normalizedItem,
      input.updatedAt,
    );
    if (providerExtensionModels.length > 0) {
      assetProviderExtensions.push(...providerExtensionModels);
    }
  }

  return {
    asset,
    assetEquityDetails,
    assetPrice: [],
    assetProviderExtensions,
    assetTaxonomy,
  };
}

function toAssetModelFromOverlayItem(
  item: AssetOverlayItem,
  updatedAt: number,
): AssetModel | null {
  const metadata = item.metadata;
  if (!metadata) {
    return null;
  }

  const parsed = parseAssetId(item.assetId);
  const address = metadata.address ?? parsed?.address ?? null;
  const chainId = metadata.networkId ?? parsed?.chainId ?? null;
  if (!address || !chainId) {
    return null;
  }

  const imageUrl =
    metadata.info?.imageLargeUrl ??
    metadata.info?.imageSmallUrl ??
    metadata.info?.imageThumbUrl ??
    null;

  return {
    address,
    assetId: item.assetId,
    chainId,
    decimals: metadata.decimals ?? null,
    id: toAssetDomainCollectionId(item.assetId),
    imageUrl,
    name: metadata.name,
    symbol: metadata.symbol,
    updatedAt,
  };
}

function normalizeResponseAssetId(assetId: string): string | null {
  const [normalizedAssetId] = normalizeAssetDomainAssetIds([
    tryAssetIdLikeToLegacyAssetId(assetId) ?? assetId,
  ]);

  return normalizedAssetId ?? null;
}

function normalizeAssetOverlayItem(
  item: AssetOverlayItem,
): AssetOverlayItem | null {
  const assetId = normalizeResponseAssetId(item.assetId);
  if (!assetId) {
    return null;
  }

  return {
    ...item,
    assetId,
  };
}

function toAssetModelFromAssetDetails(input: {
  assetId: string;
  response: AssetDetailsOutput;
  updatedAt: number;
}): AssetModel | null {
  const metadata = input.response.metadata;
  const marketToken = input.response.marketData.token;
  const address = metadata?.address ?? marketToken.address;
  const chainId = metadata?.networkId ?? marketToken.networkId;
  const name = metadata?.name ?? marketToken.name;
  const symbol = metadata?.symbol ?? marketToken.symbol;
  const imageUrl =
    metadata?.info?.imageLargeUrl ??
    metadata?.info?.imageSmallUrl ??
    metadata?.info?.imageThumbUrl ??
    marketToken.info?.imageLargeUrl ??
    marketToken.info?.imageSmallUrl ??
    marketToken.info?.imageThumbUrl ??
    null;

  if (!address || !chainId || !name || !symbol) {
    return null;
  }

  return {
    address,
    assetId: input.assetId,
    chainId,
    decimals: metadata?.decimals ?? marketToken.decimals ?? null,
    id: toAssetDomainCollectionId(input.assetId),
    imageUrl,
    name,
    symbol,
    updatedAt: input.updatedAt,
  };
}

function toAssetPriceModelFromAssetDetails(input: {
  assetId: string;
  response: AssetDetailsOutput;
  updatedAt: number;
}): AssetPriceModel | null {
  const marketData = input.response.marketData;

  return toAssetPriceModel({
    assetId: input.assetId,
    buyCount1: marketData.buyCount1,
    change1: marketData.change1,
    change24: marketData.change24,
    circulatingMarketCap: marketData.circulatingMarketCap,
    high1: marketData.high1,
    liquidity: marketData.liquidity,
    marketCap: marketData.marketCap,
    priceUsd: marketData.priceUSD,
    quoteToken: marketData.quoteToken ?? null,
    txnCount1: marketData.txnCount1,
    uniqueTransactions1: marketData.uniqueTransactions1,
    updatedAt: input.updatedAt,
    volume1: marketData.volume1,
    volume24: marketData.volume24,
  });
}

function toAssetModelFromBrowseAssetItem(
  item: BrowseAssetItem,
  updatedAt: number,
): AssetModel | null {
  const metadata = item.metadata;
  const token = item.marketData.token;
  const imageUrl =
    metadata?.info?.iconUrl ??
    token.info?.iconUrl ??
    metadata?.info?.imageLargeUrl ??
    metadata?.info?.imageSmallUrl ??
    metadata?.info?.imageThumbUrl ??
    token.info?.imageLargeUrl ??
    token.info?.imageSmallUrl ??
    token.info?.imageThumbUrl ??
    null;

  return {
    address: token.address,
    assetId: item.assetId,
    chainId: token.networkId,
    decimals: metadata?.decimals ?? token.decimals ?? null,
    id: toAssetDomainCollectionId(item.assetId),
    imageUrl,
    name: metadata?.name ?? token.name,
    symbol: metadata?.symbol ?? token.symbol,
    updatedAt,
  };
}

function toAssetPriceModelFromBrowseAssetItem(
  item: BrowseAssetItem,
  updatedAt: number,
): AssetPriceModel | null {
  return toAssetPriceModel({
    assetId: item.assetId,
    buyCount1: item.marketData.buyCount1,
    change1: item.marketData.change1,
    change24: item.marketData.change24,
    circulatingMarketCap: item.marketData.circulatingMarketCap,
    high1: item.marketData.high1,
    liquidity: item.marketData.liquidity,
    marketCap: item.marketData.marketCap,
    priceUsd: item.marketData.priceUSD,
    quoteToken: item.marketData.quoteToken ?? null,
    updatedAt,
    txnCount1: item.marketData.txnCount1,
    uniqueTransactions1: item.marketData.uniqueTransactions1,
    volume1: item.marketData.volume1,
    volume24: item.marketData.volume24,
  });
}

function toBrowseAssetTaxonomyModel(
  item: BrowseAssetItem,
  updatedAt: number,
): AssetTaxonomyModel | null {
  return {
    ...item.taxonomy,
    assetId: item.assetId,
    id: toAssetDomainCollectionId(item.assetId),
    updatedAt,
  };
}

function toBrowseAssetProviderExtensionModels(
  item: BrowseAssetItem,
  updatedAt: number,
): AssetProviderExtensionModel[] {
  if (!item.marketData.ondo) {
    return [];
  }

  return [
    {
      assetId: item.assetId,
      extension: item.marketData.ondo,
      id: toAssetProviderExtensionId(item.assetId, "ondo"),
      provider: "ondo",
      updatedAt,
    },
  ];
}

function toAssetModelFromStocksPageRow(
  row: StocksPageRow,
  updatedAt: number,
): AssetModel | null {
  return {
    address: row.address,
    assetId: row.assetId,
    chainId: row.chainId,
    decimals: null,
    id: toAssetDomainCollectionId(row.assetId),
    imageUrl: row.iconUrl ?? row.imageUrl ?? null,
    name: row.displayName,
    symbol: row.onchainSymbol,
    updatedAt,
  };
}

function toAssetPriceModelFromStocksPageRow(
  row: StocksPageRow,
  updatedAt: number,
): AssetPriceModel | null {
  return toAssetPriceModel({
    assetId: row.assetId,
    buyCount1: null,
    change1: null,
    change24:
      typeof row.change24Pct === "number" ? row.change24Pct / 100 : null,
    circulatingMarketCap: row.marketCap,
    high1: null,
    liquidity: null,
    marketCap: row.marketCap,
    priceUsd: row.priceUsd,
    quoteToken: "USD",
    txnCount1: null,
    uniqueTransactions1: null,
    updatedAt,
    volume1: null,
    volume24: row.volume24Unit === "usd" ? row.volume24 : null,
  });
}

function toStocksPageProviderExtensionModels(
  row: StocksPageRow,
  updatedAt: number,
): AssetProviderExtensionModel[] {
  if (!row.ondoStatus) {
    return [];
  }

  return [
    {
      assetId: row.assetId,
      extension: {
        ...row.ondoStatus,
        sparkline24h: [...row.sparkline24h],
        tags: [...row.tags],
        underlyingVolume24h:
          row.volume24Unit === "shares" ? String(row.volume24) : null,
      },
      id: toAssetProviderExtensionId(row.assetId, "ondo"),
      provider: "ondo",
      updatedAt,
    },
  ];
}

function toAssetTaxonomyModel(
  item: AssetOverlayItem,
  updatedAt: number,
): AssetTaxonomyModel | null {
  if (!item.taxonomy) {
    return null;
  }

  return {
    ...item.taxonomy,
    assetId: item.assetId,
    id: toAssetDomainCollectionId(item.assetId),
    updatedAt,
  };
}

function toAssetEquityDetailsModel(
  item: AssetOverlayItem,
  updatedAt: number,
): AssetEquityDetailsModel | null {
  if (!item.equityDetails) {
    return null;
  }

  return {
    ...item.equityDetails,
    assetId: item.assetId,
    id: toAssetDomainCollectionId(item.assetId),
    updatedAt,
  };
}

function toAssetProviderExtensionModels(
  item: AssetOverlayItem,
  updatedAt: number,
): AssetProviderExtensionModel[] {
  if (!item.providerExtensions.ondo) {
    return [];
  }

  return [
    {
      assetId: item.assetId,
      extension: item.providerExtensions.ondo,
      id: toAssetProviderExtensionId(item.assetId, "ondo"),
      provider: "ondo",
      updatedAt,
    },
  ];
}

function parseAssetId(assetId: string): {
  address: string;
  chainId: number;
} | null {
  const delimiterIndex = assetId.lastIndexOf(":");
  if (delimiterIndex <= 0 || delimiterIndex >= assetId.length - 1) {
    return null;
  }

  const address = assetId.slice(0, delimiterIndex);
  const chainId = Number.parseInt(assetId.slice(delimiterIndex + 1), 10);
  if (address.length === 0 || !Number.isFinite(chainId)) {
    return null;
  }

  return {
    address,
    chainId,
  };
}

function parseMetric(value: number | string | null | undefined): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toAssetPriceModel(input: {
  assetId: string;
  buyCount1: number | string | null | undefined;
  change1: number | string | null | undefined;
  change24: number | string | null | undefined;
  circulatingMarketCap: number | string | null | undefined;
  high1: number | string | null | undefined;
  liquidity: number | string | null | undefined;
  marketCap: number | string | null | undefined;
  priceUsd: number | string | null | undefined;
  quoteToken: string | null | undefined;
  txnCount1: number | string | null | undefined;
  uniqueTransactions1: number | string | null | undefined;
  updatedAt: number;
  volume1: number | string | null | undefined;
  volume24: number | string | null | undefined;
}): AssetPriceModel {
  return {
    assetId: input.assetId,
    buyCount1: parseMetric(input.buyCount1),
    change1Percent: toPercentMetric(input.change1),
    change24Percent: toPercentMetric(input.change24),
    circulatingMarketCap: parseMetric(input.circulatingMarketCap),
    high1Usd: parseMetric(input.high1),
    id: toAssetDomainCollectionId(input.assetId),
    liquidity: parseMetric(input.liquidity),
    marketCap: parseMetric(input.marketCap),
    priceUsd: parseMetric(input.priceUsd),
    quoteToken: input.quoteToken ?? null,
    txnCount1: parseMetric(input.txnCount1),
    uniqueTransactions1: parseMetric(input.uniqueTransactions1),
    updatedAt: input.updatedAt,
    volume1: parseMetric(input.volume1),
    volume24: parseMetric(input.volume24),
  };
}

function toPercentMetric(
  value: number | string | null | undefined,
): number | null {
  const parsed = parseMetric(value);
  return parsed === null ? null : parsed * 100;
}
